import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import Badge from '../../components/common/Badge';
import { getCompanyProfile, updateCompanyProfile } from '../../api/company';
import { useToast } from '../../context/ToastContext';

const profileSchema = z.object({
  name: z.string().min(2, 'Company name is required'),
  website: z.string().url('Must be a valid URL'),
  industry: z.string().min(1, 'Industry is required'),
  description: z.string().min(10, 'Description is required'),
  location: z.string().min(2, 'Location is required'),
  hr_name: z.string().min(2, 'HR Name is required'),
  hr_contact: z.string().min(10, 'Valid phone required'),
});

const CompanyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm({
    resolver: zodResolver(profileSchema)
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await getCompanyProfile();
      setProfile(res.data);
      reset(res.data);
    } catch (err) {
      addToast('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      await updateCompanyProfile(data);
      setProfile({ ...profile, ...data });
      setIsEditing(false);
      addToast('Profile updated successfully', 'success');
    } catch (err) {
      addToast('Failed to update profile', 'error');
    }
  };

  if (loading) return <div className="page-wrapper"><Navbar /><Loader fullPage /><Footer /></div>;

  return (
    <div className="page-wrapper dashboard-page">
      <Navbar />
      
      <main className="container pt-8 pb-20 flex-1 max-w-4xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="heading-1">Company Profile</h1>
            <p className="subheading">Manage your company details and public presence.</p>
          </div>
          {!isEditing && (
            <Button variant="primary" onClick={() => setIsEditing(true)}>Edit Profile</Button>
          )}
        </div>

        <div className="card-elevated">
          {profile?.status === 'pending' && (
            <div className="alert alert-warning mb-8">
              <span className="text-xl">⚠️</span>
              <div>
                <strong>Account Status: Pending Approval</strong>
                <p className="mt-1 text-sm">Your account is currently under review by our team. You can edit your profile while waiting.</p>
              </div>
            </div>
          )}

          {!isEditing ? (
            <div className="grid md:grid-cols-3 gap-8">
              <div className="col-span-1 border-r border-gray-100 pr-8">
                <div className="w-24 h-24 bg-gray-900 text-white rounded-xl flex items-center justify-center text-4xl font-bold mb-6 mx-auto md:mx-0">
                  {profile.name?.charAt(0)}
                </div>
                <h2 className="heading-3 mb-1 text-center md:text-left">{profile.name}</h2>
                <p className="text-gray-500 text-sm mb-6 text-center md:text-left">{profile.industry}</p>
                
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="text-xs font-semi text-gray-400 uppercase">Website</div>
                    <a href={profile.website} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm">{profile.website}</a>
                  </div>
                  <div>
                    <div className="text-xs font-semi text-gray-400 uppercase">Location</div>
                    <div className="text-gray-900 text-sm">{profile.location}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semi text-gray-400 uppercase">Account Email</div>
                    <div className="text-gray-900 text-sm">{profile.email}</div>
                  </div>
                </div>
              </div>
              
              <div className="col-span-2 pl-0 md:pl-4">
                <div className="mb-8">
                  <h3 className="text-lg font-semi mb-3 border-b border-gray-100 pb-2">About Company</h3>
                  <div className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                    {profile.description}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semi mb-3 border-b border-gray-100 pb-2">HR Contact Info</h3>
                  <div className="grid grid-2 gap-4">
                    <div>
                      <div className="text-xs font-semi text-gray-400 uppercase mb-1">Contact Name</div>
                      <div className="text-gray-900 text-sm font-medium">{profile.hr_name}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semi text-gray-400 uppercase mb-1">Contact Phone</div>
                      <div className="text-gray-900 text-sm font-medium">{profile.hr_contact}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-2 gap-x-6">
                <Input
                  label="Company Name"
                  {...register('name')}
                  error={errors.name?.message}
                />
                <Input
                  label="Industry"
                  {...register('industry')}
                  error={errors.industry?.message}
                />
                <Input
                  label="Website URL"
                  {...register('website')}
                  error={errors.website?.message}
                />
                <Input
                  label="Headquarters Location"
                  {...register('location')}
                  error={errors.location?.message}
                />
              </div>
              
              <div className="form-group my-6">
                <label className="form-label">Company Description</label>
                <textarea 
                  className={`form-input textarea ${errors.description ? 'error' : ''}`}
                  style={{ minHeight: '120px' }}
                  {...register('description')}
                ></textarea>
                {errors.description && <span className="form-error">{errors.description.message}</span>}
              </div>
              
              <h3 className="text-lg font-semi mt-8 mb-4 border-b border-gray-100 pb-2">HR Contact Details</h3>
              <div className="grid grid-2 gap-x-6">
                <Input
                  label="Contact Name"
                  {...register('hr_name')}
                  error={errors.hr_name?.message}
                />
                <Input
                  label="Contact Phone"
                  {...register('hr_contact')}
                  error={errors.hr_contact?.message}
                />
              </div>
              
              <div className="border-t border-gray-100 mt-8 pt-6 flex justify-end gap-4">
                <Button type="button" variant="ghost" onClick={() => { reset(profile); setIsEditing(false); }}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isSubmitting}>
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyProfile;